import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  Stack,
  Tooltip,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
// import AddIcon from '@mui/icons-material/Add';       // commented out — Add disabled for now
// import DeleteIcon from '@mui/icons-material/Delete'; // commented out — Delete disabled for now

// ─── Adjustment badges ────────────────────────────────────────────────────────

const PctChip = ({ value }) => {
  if (!value) return <Chip label="—" size="small" variant="outlined" />;
  const num = Number(value);
  return <Chip label={`${num > 0 ? '+' : ''}${num}%`} size="small" color={num < 0 ? 'error' : 'success'} variant="filled" />;
};

const AdjChip = ({ value }) => {
  if (!value) return <Chip label="—" size="small" variant="outlined" />;
  return <Chip label={`€${Number(value).toFixed(2)}`} size="small" color="primary" variant="filled" />;
};

// ─── Edit Form Modal ──────────────────────────────────────────────────────────

const HaulageCategoryEditModal = ({ open, onClose, onSave, editData }) => {
  const [form, setForm] = useState({ priceChangePCT: '', priceChangeAdj: '', comments: '' });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open && editData) {
      setForm({
        priceChangePCT: editData.priceChangePCT ?? '',
        priceChangeAdj: editData.priceChangeAdj ?? '',
        comments: editData.comments ?? '',
      });
      setErrors({});
    }
  }, [open, editData]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const pctActive = form.priceChangePCT !== '' && Number(form.priceChangePCT) !== 0;
  const adjActive = form.priceChangeAdj !== '' && Number(form.priceChangeAdj) !== 0;

  const validate = () => {
    const errs = {};
    if (form.priceChangePCT !== '' && isNaN(Number(form.priceChangePCT))) errs.priceChangePCT = 'Must be a number';
    if (form.priceChangeAdj !== '' && isNaN(Number(form.priceChangeAdj))) errs.priceChangeAdj = 'Must be a number';
    return errs;
  };

  const handleSubmit = () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    onSave({
      ...editData,
      priceChangePCT: form.priceChangePCT !== '' ? Number(form.priceChangePCT) : 0,
      priceChangeAdj: form.priceChangeAdj !== '' ? Number(form.priceChangeAdj) : 0,
      comments: form.comments,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Edit — {editData?.haulageCategory}
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Box px={3} pt={1} pb={2}>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          textTransform="uppercase"
          display="block"
          mb={1}>
          Price Adjustments
        </Typography>

        <Stack direction="row" spacing={2}>
          <TextField
            fullWidth
            size="small"
            type="number"
            name="priceChangePCT"
            label="Price Change %"
            value={form.priceChangePCT}
            onChange={handleChange}
            disabled={adjActive}
            error={Boolean(errors.priceChangePCT)}
            helperText={adjActive ? 'Clear adjustment first' : errors.priceChangePCT}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          />
          <TextField
            fullWidth
            size="small"
            type="number"
            name="priceChangeAdj"
            label="Price Adjustment €"
            value={form.priceChangeAdj}
            onChange={handleChange}
            disabled={pctActive}
            error={Boolean(errors.priceChangeAdj)}
            helperText={pctActive ? 'Clear % change first' : errors.priceChangeAdj}
            InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
          />
        </Stack>

        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          textTransform="uppercase"
          display="block"
          mt={2.5}
          mb={1}>
          Comments (optional)
        </Typography>
        <TextField
          fullWidth
          size="small"
          name="comments"
          label="Comments"
          value={form.comments}
          onChange={handleChange}
          multiline
          rows={2}
        />
      </Box>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit}>
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Manager Dialog ──────────────────────────────────────────────────────

const HaulageCategoryManagerDialog = ({
  open,
  onClose,
  haulageCategories,
  fetchHaulageCategories,
  updateHaulageCategory,
  // addHaulageCategory,    // commented out — disabled for now
  // deleteHaulageCategory, // commented out — disabled for now
}) => {
  const [editRow, setEditRow] = useState(null);

  useEffect(() => {
    if (open) fetchHaulageCategories();
  }, [open, fetchHaulageCategories]);

  const handleSave = async data => {
    try {
      await updateHaulageCategory(data);
      setEditRow(null);
    } catch (err) {
      console.error('Update failed', err);
    }
  };

  const sorted = [...(haulageCategories || [])].sort((a, b) => a.haulageCategory.localeCompare(b.haulageCategory));

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Manage Haulage Categories
          <Box display="flex" gap={2} alignItems="center">
            {/* Add button — commented out for now
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
              Add Category
            </Button> */}
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <Box px={3} py={2}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                <th style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 600, color: '#666' }}>Category</th>
                <th style={{ textAlign: 'center', padding: '8px 10px', fontWeight: 600, color: '#666' }}>Rate Change(%)</th>
                <th style={{ textAlign: 'center', padding: '8px 10px', fontWeight: 600, color: '#666' }}>Rate Adj (€)</th>
                <th style={{ textAlign: 'center', padding: '8px 10px', fontWeight: 600, color: '#666' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: '24px', color: '#999' }}>
                    No haulage categories found.
                  </td>
                </tr>
              )}
              {sorted.map(row => (
                <tr key={row.id} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                  <td style={{ padding: '9px 10px' }}>
                    <Typography variant="body2" fontWeight={500}>
                      {row.haulageCategory}
                    </Typography>
                    {row.comments && (
                      <Typography variant="caption" color="text.secondary">
                        {row.comments}
                      </Typography>
                    )}
                  </td>
                  <td style={{ padding: '9px 10px', textAlign: 'center' }}>
                    <PctChip value={row.priceChangePCT} />
                  </td>
                  <td style={{ padding: '9px 10px', textAlign: 'center' }}>
                    <AdjChip value={row.priceChangeAdj} />
                  </td>
                  <td style={{ padding: '9px 10px', textAlign: 'center' }}>
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title="Edit">
                        <Button size="small" variant="outlined" color="primary" onClick={() => setEditRow(row)}>
                          <EditIcon sx={{ fontSize: 15, mr: 0.5 }} /> Edit
                        </Button>
                      </Tooltip>
                      {/* Delete button — commented out for now
                      <Tooltip title="Delete">
                        <Button size="small" variant="outlined" color="error" onClick={() => setDeleteRow(row)}>
                          <DeleteIcon sx={{ fontSize: 15, mr: 0.5 }} /> Delete
                        </Button>
                      </Tooltip> */}
                    </Stack>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <HaulageCategoryEditModal
        open={Boolean(editRow)}
        onClose={() => setEditRow(null)}
        onSave={handleSave}
        editData={editRow}
      />

      {/* Delete confirm — commented out for now
      <HaulageCategoryDeleteModal
        open={Boolean(deleteRow)}
        onClose={() => setDeleteRow(null)}
        onConfirm={handleDelete}
        row={deleteRow}
      /> */}
    </>
  );
};

export default HaulageCategoryManagerDialog;
